import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  TrendingUp,
  Calendar,
  Wallet,
  Calculator as CalcIcon,
  Sparkles,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import merchantImg from "@/assets/merchant.webp";

export const Route = createFileRoute("/calculator")({
  head: () => ({
    meta: [
      { title: "Earnings Calculator — Spayzo" },
      { name: "description", content: "Estimate how much you can earn hosting your space on Spayzo. Adjust rate, hours, and active days to see your monthly payout." },
      { property: "og:title", content: "Earnings Calculator — Spayzo" },
      { property: "og:description", content: "See your projected monthly payout as a Spayzo host with our interactive earnings calculator." },
    ],
  }),
  component: CalculatorPage,
});

const SERVICE_FEE = 0.15;

const benefitKeys = [
  { icon: TrendingUp, key: "avg" },
  { icon: Calendar, key: "cal" },
  { icon: Wallet, key: "payouts" },
  { icon: ShieldCheck, key: "damage" },
  { icon: Users, key: "reach" },
] as const;

function CalculatorPage() {
  const { t, i18n } = useTranslation();
  const [rate, setRate] = useState(45);
  const [hours, setHours] = useState(6);
  const [days, setDays] = useState(18);
  const [addons, setAddons] = useState(120);

  const { gross, fee, net, yearly } = useMemo(() => {
    const g = rate * hours * days + addons;
    const f = g * SERVICE_FEE;
    const n = g - f;
    return { gross: g, fee: f, net: n, yearly: n * 12 };
  }, [rate, hours, days, addons]);

  const fmt = (n: number) =>
    n.toLocaleString(i18n.language === "ar" ? "ar-SA" : "en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="mx-auto max-w-7xl px-4 pb-10 pt-12 sm:px-6 lg:px-8 lg:pt-16">
          <div className="overflow-hidden rounded-[2rem] border border-border bg-gradient-to-br from-[oklch(0.18_0.02_260)] to-[oklch(0.22_0.04_260)] text-white shadow-elevated lg:rounded-[2.5rem]">
            <div className="grid gap-0 lg:grid-cols-2">
              <div className="p-8 sm:p-12 lg:p-16">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/80 backdrop-blur">
                  <span className="flex h-1.5 w-1.5 rounded-full bg-[oklch(0.75_0.18_140)]" />
                  {t("calculator.badge")}
                </div>
                <h1 className="mt-6 text-balance font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                  {t("calculator.titleA")}{" "}
                  <span className="bg-gradient-to-r from-[oklch(0.85_0.15_90)] to-[oklch(0.8_0.18_60)] bg-clip-text text-transparent">
                    {t("calculator.titleB")}
                  </span>
                </h1>
                <p className="mt-5 max-w-md text-base leading-relaxed text-white/70">{t("calculator.subtitle")}</p>

                <ul className="mt-8 space-y-4">
                  {benefitKeys.map((b) => (
                    <li key={b.key} className="flex items-start gap-3 text-sm text-white/85">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white">
                        <b.icon className="h-4 w-4" />
                      </div>
                      <span className="pt-1">{t(`calculator.benefits.${b.key}`)}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <Link
                    to="/partner"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-foreground transition-transform hover:scale-[1.02]"
                  >
                    {t("calculator.startListing")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
                  >
                    {t("calculator.viewPlans")}
                  </Link>
                </div>
              </div>

              <div className="relative min-h-[280px] lg:min-h-full">
                <img
                  src={merchantImg}
                  alt={t("calculator.heroAlt")}
                  width={1200}
                  height={1400}
                  loading="lazy"
                decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.18_0.02_260)] via-transparent to-transparent lg:from-[oklch(0.18_0.02_260)]/40" />
                <div className="absolute bottom-6 start-6 end-6 rounded-2xl border border-white/10 bg-white/95 p-5 shadow-elevated backdrop-blur sm:bottom-10 sm:start-10 sm:end-auto sm:max-w-[260px]">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("calculator.live")}</div>
                  <div className="mt-1 font-display text-3xl font-bold text-foreground">{fmt(net)}</div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[oklch(0.94_0.08_140)] px-2 py-0.5 text-[oklch(0.4_0.15_140)]">
                      <TrendingUp className="h-3 w-3" /> {t("calculator.monthly")}
                    </span>
                    {t("calculator.afterFees")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
              <div className="flex items-center gap-2">
                <CalcIcon className="h-5 w-5 text-[#5958F3]" />
                <h2 className="font-display text-2xl font-semibold">{t("calculator.inputs")}</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t("calculator.inputsHint")}</p>

              <div className="mt-8 space-y-7">
                <Field
                  label={t("calculator.rate")}
                  value={t("calculator.ratePer", { amount: fmt(rate) })}
                  sliderValue={rate}
                  min={10}
                  max={500}
                  step={5}
                  onChange={setRate}
                />
                <Field
                  label={t("calculator.hours")}
                  value={`${hours} ${t("calculator.hoursSuffix")}`}
                  sliderValue={hours}
                  min={1}
                  max={12}
                  step={1}
                  onChange={setHours}
                />
                <Field
                  label={t("calculator.days")}
                  value={`${days} ${t("calculator.daysSuffix")}`}
                  sliderValue={days}
                  min={1}
                  max={30}
                  step={1}
                  onChange={setDays}
                />

                <div>
                  <div className="flex items-baseline justify-between">
                    <Label className="text-sm font-medium">{t("calculator.addons")}</Label>
                    <span className="text-sm font-semibold">{fmt(addons)}</span>
                  </div>
                  <Input
                    aria-label={t("calculator.addons")}
                    type="number"
                    min={0}
                    value={addons}
                    onChange={(e) => setAddons(Number(e.target.value) || 0)}
                    className="mt-3"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">{t("calculator.addonsHint")}</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-gradient-to-br from-[#5958F3]/5 to-transparent p-6 sm:p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Sparkles className="h-4 w-4 text-[#5958F3]" />
                {t("calculator.estimated")}
              </div>
              <div className="mt-3 font-display text-5xl font-bold text-foreground">
                {fmt(net)}
                <span className="ms-2 text-base font-medium text-muted-foreground">{t("calculator.perMonth")}</span>
              </div>

              <div className="mt-6 space-y-2 rounded-2xl border border-border bg-surface p-5 text-sm">
                <Row label={t("calculator.gross")} value={fmt(gross)} />
                <Row label={t("calculator.fee", { pct: Math.round(SERVICE_FEE * 100) })} value={`− ${fmt(fee)}`} />
                <div className="my-2 h-px bg-border" />
                <Row label={t("calculator.net")} value={fmt(net)} bold />
                <Row label={t("calculator.yearlyRow")} value={fmt(yearly)} bold />
              </div>

              <Link
                to="/partner"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#5958F3] px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                {t("calculator.startCta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
              </Link>

              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t("calculator.disclaimer")}</p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-semibold">{t("calculator.howTitle")}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {(["gross", "fee", "payout"] as const).map((k) => (
              <div key={k} className="rounded-2xl border border-border bg-card p-6">
                <div className="font-display text-lg font-semibold">{t(`calculator.how.${k}Title`)}</div>
                <p className="mt-2 text-sm text-muted-foreground">{t(`calculator.how.${k}Body`)}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  value,
  sliderValue,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: string;
  sliderValue: number;
  min: number;
  max: number;
  step: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-sm font-semibold">{value}</span>
      </div>
      <Slider
        aria-label={label}
        className="mt-3"
        value={[sliderValue]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
      />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "text-foreground" : "text-muted-foreground"}>{label}</span>
      <span className={bold ? "font-semibold text-foreground" : "font-medium text-foreground"}>{value}</span>
    </div>
  );
}
